import React, { useRef, useState } from 'lib/react';
import {
  EVButton,
  EVClear,
  EVChip,
  EVGrid,
  EVIconButton,
  EVKeyboardArrowDown,
  EVKeyboardArrowUp,
  EVOrgHierarchyIcon,
  EVPaper,
  EVSearchIcon,
  EVTreeView,
  EVSkeleton,
} from 'lib/components';
import TextInputField from 'commonComponents/textInputField';
import { useTranslation } from 'lib/translation';
import { filterOrgNodes, getSelectedParentNodeIds } from 'features/orderHistory/OrderHistory.utils';
import PropTypes from 'prop-types';
import Popper from 'commonComponents/popper';
import { useDispatch, useSelector } from 'react-redux';
import { getOrgDescendants } from 'features/orderHistory/OrderHistory.actions';
import OrgNodesFilterStyles from './OrgNodesFilterStyles';
import OrgNodes from './OrgNodes.component';
import ClearButton from '../../commonComponents/clearButton';

function OrgNodesFilter(props) {
  const dispatch = useDispatch();
  const { rootOrgNode, selectedNodeIds, setSelectedNodeIds } = props;

  const classes = OrgNodesFilterStyles();
  const { t } = useTranslation();

  const orgDescendantsLoading = useSelector(({ userData }) => userData.loading.orgDescendants);
  const orgDescendantsError = useSelector(({ userData }) => userData.error.orgDescendants);

  const [searchInput, setSearchInput] = useState('');
  const [showOrgNodes, setShowOrgNodes] = useState(false);
  const orgsButtonContainerRef = useRef();
  const handleSearchInputChange = ({ target: { value } }) => setSearchInput(value);

  const handleOrgNodeClick = () => {
    setShowOrgNodes((show) => !show);
    dispatch(getOrgDescendants());
  };

  const handleClickAway = () => setShowOrgNodes(false);

  const trimmedSearchInput = searchInput.trim();
  const [filteredRootNode, expandedNodes] = filterOrgNodes(rootOrgNode, trimmedSearchInput);
  const selectedParentNodeIds = rootOrgNode && getSelectedParentNodeIds(rootOrgNode, selectedNodeIds);

  const selectedOrgsCount = selectedNodeIds.length;

  const handleClearSelectedOrgs = () => {
    setSelectedNodeIds([]);
  };

  const orgNodeError = () => (
    <EVGrid style={{ padding: '30px', margin: 'auto' }}>{t('order_history__orgs_error')}</EVGrid>
  );
  const orgNodeLoader = () => (
    <EVGrid style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <EVSkeleton style={{ height: '38px', width: '100%', borderRadius: '6px' }} animation="wave" variant="rect" />
      <EVGrid style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <EVSkeleton style={{ height: '56px', width: '100%', borderRadius: '6px' }} animation="wave" variant="rect" />
        <EVSkeleton style={{ height: '56px', width: '100%', borderRadius: '6px' }} animation="wave" variant="rect" />
        <EVSkeleton style={{ height: '56px', width: '100%', borderRadius: '6px' }} animation="wave" variant="rect" />
      </EVGrid>
    </EVGrid>
  );

  const renderTree = () => {
    if (trimmedSearchInput && trimmedSearchInput.length < 3) return t('order_history__orgs_type_3_chars_to_search');

    if (!filteredRootNode) return t('order_history__orgs_no_result', { trimmedSearchInput });

    return (
      <EVTreeView
        key={expandedNodes}
        className={classes.orgNodesTreeRoot}
        defaultCollapseIcon={<EVKeyboardArrowUp />}
        defaultExpandIcon={<EVKeyboardArrowDown />}
        defaultExpanded={searchInput ? expandedNodes : selectedParentNodeIds}
        disableSelection
      >
        <OrgNodes
          node={filteredRootNode}
          selectedNodeIds={selectedNodeIds}
          setSelectedNodeIds={setSelectedNodeIds}
          trimmedSearchInput={trimmedSearchInput}
          isEvenDepth
          isFirst
        />
      </EVTreeView>
    );
  };

  // #genai: extract nested ternary into an independent helper function (Sonar S3358); evaluation order/behavior unchanged
  const renderOrgNodesPaperContent = () => {
    if (orgDescendantsError) return orgNodeError();
    if (orgDescendantsLoading) return orgNodeLoader();
    return (
      <>
        <TextInputField
          inputId="org-search-input"
          inputFieldProps={{
            autoComplete: 'off',
            placeholder: t('order_history__search_btn'),
            InputProps: {
              className: classes.searchInput,
              endAdornment: (
                <>
                  {(searchInput.length > 0) && (
                  <EVIconButton
                    id="clear-org-search-input"
                    onClick={() => setSearchInput('')}
                    style={{ padding: 0 }}
                  >
                    <EVClear />
                  </EVIconButton>
                  ) }
                  <EVSearchIcon htmlColor="#B4BAC7" />
                </>
              ),
            },
          }}
          inputValue={searchInput}
          handleChange={handleSearchInputChange}
        />
        {renderTree()}
      </>
    );
  };

  return (
    <EVGrid ref={orgsButtonContainerRef} className={classes.orgNodesContainer}>
      <EVButton
        id="org-nodes-button"
        variant="outlined"
        onClick={handleOrgNodeClick}
        style={{ width: '100%', padding: '8px', height: '44px' }}
        classes={{ label: classes.orgButtonLabel }}
      >
        <EVGrid component="span" className={classes.orgButtonLabelLeft}>
          <EVOrgHierarchyIcon htmlColor="#82899C" />
          {t('order_history__orgs')}
        </EVGrid>
        <EVGrid component="span" className={classes.orgButtonLabelRight}>
          {showOrgNodes ? <EVKeyboardArrowUp htmlColor="#4D5365" /> : <EVKeyboardArrowDown htmlColor="#4D5365" />}
          {selectedOrgsCount > 0 && (
          <>
            <EVChip label={selectedOrgsCount} className={classes.selectedOrgsCountChip} />
            <ClearButton id="clear-selected-orgs" handleClear={handleClearSelectedOrgs} />
          </>
          )}
        </EVGrid>
      </EVButton>
      <Popper
        popperId="org-nodes-popper"
        anchorEl={orgsButtonContainerRef.current}
        className={classes.orgNodesPopper}
        placement="bottom-end"
        open={showOrgNodes}
        modifiers={{ offset: { offset: '0,5px' } }}
        onClickAway={handleClickAway}
      >
        <EVPaper className={classes.orgNodesPaper}>
          {renderOrgNodesPaperContent()}
        </EVPaper>
      </Popper>
    </EVGrid>
  );
}

OrgNodesFilter.defaultProps = {
  rootOrgNode: {},
  selectedNodeIds: [],
  setSelectedNodeIds: () => {},
};

OrgNodesFilter.propTypes = {
  rootOrgNode: PropTypes.shape({
    orgId: PropTypes.number,
    orgName: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape()),
    userNotAuthorized: PropTypes.bool,
  }),
  selectedNodeIds: PropTypes.arrayOf(PropTypes.number),
  setSelectedNodeIds: PropTypes.func,
};

export default OrgNodesFilter;
